import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  SegmentAttributeGroup,
  SegmentDetailAttributeDate,
  SegmentDetailAttributeOptions,
  SegmentDetailAttributeRange,
  SegmentDetailAttributeTransactional,
  SegmentDetailParsedAttributeGroup,
  SegmentDetailResponse,
  SegmentDetailResponseAttributeGroup,
  SegmentDetailResponseParsed,
  SegmentFilterOperator,
  SelectedAttribute
} from '../models/segments';
import { AppContext, AppProviderDispatch } from '../shared/app-context';
import useRestService from '../shared/rest-service';
import { mapTransactionalAttributes } from '../utilities/attribute-filter';
import _ from 'lodash';

/**
 * Fetch configuration details of a single segment and map to format required by the segment builder components
 */
export function useSegment(segmentId: string) {
  const appState = React.useContext<AppProviderDispatch>(AppContext);
  const { fetchWrapper } = useRestService();

  const userInterface = window.env.USER_INTERFACE ?? 'default';

  const segmentQuery = useQuery<SegmentDetailResponse, Error, SegmentDetailResponseParsed>(
    ['segment', segmentId, userInterface, appState.state.clientId],
    () => {
      if (userInterface === 'management') {
        return fetchWrapper(`api/segments/${segmentId}`);
      }
      return fetchWrapper(`api/${appState.state.clientId}/segments/${segmentId}`);
    },
    {
      select: (data) => {
        const newData = {
          ...data,
          attributes: {
            include: {
              ...data.attributes.include,
              predicates: mapTransactionalAttributes(data.attributes.include.predicates)
            },
            exclude: {
              ...data.attributes.exclude,
              predicates: mapTransactionalAttributes(data.attributes.exclude?.predicates)
            }
          }
        };

        // returned attributes with all selected filters deserialized
        const includedAttributes = deserializeSelectedFilters(newData.attributes.include);
        const excludedAttributes = deserializeSelectedFilters(newData.attributes.exclude);
        // convert attributes list into SegmentGroup
        const selectedIncludedAttributes = responseGroupToAttributeGroup(includedAttributes);
        const selectedExcludedAttributes = responseGroupToAttributeGroup(excludedAttributes);
        // data to preload the results table as SegmentResults
        const results = {
          totalCustomers: newData.totalCustomers,
          includedCustomers: newData.includedCustomers,
          excludedCustomers: newData.excludedCustomers,
          perAttributeGroup: {
            include: newData.attributes.include.predicates.map((predicate) => predicate.customers),
            exclude: newData.attributes.exclude?.predicates.map((predicate) => predicate.customers)
          }
        };

        return {
          ...newData,
          attributes: {
            include: includedAttributes,
            exclude: excludedAttributes
          },
          selectedAttributes: {
            include: selectedIncludedAttributes,
            exclude: selectedExcludedAttributes
          },
          results
        };
      },
      enabled: !!segmentId
    }
  );

  return {
    segmentQuery,
    userInterface
  };
}

/**
 * Recursively map over the segment attributes and deserialize any attributes' JSON filters
 * Copy all group properties in order to not overwrite base attributes' customers property
 */
function deserializeSelectedFilters(group: SegmentDetailResponseAttributeGroup): SegmentDetailParsedAttributeGroup {
  return group
    ? {
        ...group,
        predicates: group.predicates
          ? group.predicates.map((predicate) => {
              if ('predicates' in predicate) {
                return deserializeSelectedFilters(predicate);
              }
              if (predicate.isTransactional) {
                //@ts-ignore
                const transactionalPredicates = predicate.selectedTransactionalFilters?.predicates.map((item) => {
                  return {
                    ...item,
                    selectedFilters: item.selectedFilters ? JSON.parse(item.selectedFilters) : undefined
                  };
                });
                return {
                  ...predicate,
                  selectedTransactionalFilters: transactionalPredicates
                    ? {
                        //@ts-ignore
                        operator: predicate.selectedTransactionalFilters?.operator,
                        //@ts-ignore
                        predicates: transactionalPredicates ?? []
                      }
                    : {}
                } as SegmentDetailParsedAttributeGroup;
              } else {
                return {
                  ...predicate,
                  selectedFilters: predicate.selectedFilters ? JSON.parse(predicate.selectedFilters) : undefined
                } as SegmentDetailAttributeOptions | SegmentDetailAttributeRange | SegmentDetailAttributeDate;
              }
            })
          : []
      }
    : undefined;
}

/**
 * Recursively map the segment attributes (from deserializeSelectedFilters) from SegmentDetailParsedAttributeGroup to SegmentAttributeGroup
 * Required for loading initial state when editing an existing segment
 */
function responseGroupToAttributeGroup(group: SegmentDetailParsedAttributeGroup): SegmentAttributeGroup {
  return group
    ? {
        operator: group.operator,
        predicates: group.predicates.map((predicate) => {
          if ('predicates' in predicate) {
            return responseGroupToAttributeGroup(predicate);
          }
          //@ts-ignore
          return mapAttributesToSelected(predicate);
        })
      }
    : undefined;
}

/**
 * Map parsed SegmentDetailAttribute objects with generic `selectedFilters` properties into SelectedAttributes used in segment editing/building
 */
function mapAttributesToSelected(
  attribute:
    | SegmentDetailAttributeOptions
    | SegmentDetailAttributeRange
    | SegmentDetailAttributeDate
    | SegmentDetailAttributeTransactional
): SelectedAttribute {
  const attributeBase = {
    attributeId: attribute.attributeId,
    columnName: attribute.columnName,
    name: attribute.name,
    filterOperator: attribute.filterOperator
  };

  if (attribute.isTransactional) {
    return buildTransactionalAttribute(attribute);
  }

  switch (attribute.filterType) {
    case 'options': {
      if (
        attribute.filterOperator === SegmentFilterOperator.Is ||
        attribute.filterOperator === SegmentFilterOperator['Is Not']
      ) {
        return {
          ...attributeBase,
          options: attribute.selectedFilters as string[]
        };
      } else {
        return {
          ...attributeBase,
          searchText: attribute.selectedFilters as string
        };
      }
    }
    case 'range': {
      const { min, max } = attribute.selectedFilters as { min; max };
      return {
        ...attributeBase,
        range: {
          min,
          max
        }
      };
    }
    case 'datetime': {
      if (typeof attribute.selectedFilters == 'number') {
        return {
          ...attributeBase,
          numberOfDays: attribute.selectedFilters
        };
      }
      const { min, max } = attribute.selectedFilters as { min; max };
      return {
        ...attributeBase,
        dateRange: {
          min,
          max
        }
      };
    }
    default: {
      // @ts-ignore - will only throw if a new filterType is unaccounted for
      throw new Error(`Unhandled filter type: ${attribute.filterType}`);
    }
  }
}

function buildTransactionalAttribute(attribute: SegmentDetailAttributeTransactional): SelectedAttribute {
  const selectedFilters = attribute.selectedTransactionalFilters.predicates?.map((filter: any) => {
    const filterBase = {
      columnName: filter.columnName,
      filterId: filter.filterId,
      filterOperator: filter.filterOperator,
      filterType: filter.filterType,
      name: filter.name
    };

    switch (filter.filterType) {
      case 'options': {
        if (
          filter.filterOperator === SegmentFilterOperator.Is ||
          filter.filterOperator === SegmentFilterOperator['Is Not']
        ) {
          return {
            ...filterBase,
            options: filter.selectedFilters as string[]
          };
        } else {
          return {
            ...filterBase,
            searchText: filter.selectedFilters as string
          };
        }
      }
      case 'range': {
        const { min, max } = filter.selectedFilters as { min; max };
        return {
          ...filterBase,
          range: {
            min,
            max
          }
        };
      }
      case 'datetime': {
        if (typeof filter.selectedFilters == 'number') {
          return {
            ...filterBase,
            numberOfDays: filter.selectedFilters
          };
        }
        const { min, max } = filter.selectedFilters as { min; max };
        return {
          ...filterBase,
          dateRange: {
            min,
            max
          }
        };
      }
      default: {
        // @ts-ignore - will only throw if a new filterType is unaccounted for
        throw new Error(`Unhandled filter type: ${filter.filterType}`);
      }
    }
  });

  if (!selectedFilters) {
    return {
      attributeId: attribute.attributeId,
      columnName: attribute.columnName,
      filterOperator: attribute.filterOperator,
      where: 'all',
      isTransactional: true,
      groupBy: attribute.groupBy ? JSON.parse(JSON.stringify(attribute.groupBy)) : undefined
    };
  }
  return {
    attributeId: attribute.attributeId,
    columnName: attribute.columnName,
    filterOperator: attribute.filterOperator,
    where: 'all',
    isTransactional: true,
    groupBy: attribute.groupBy ? JSON.parse(JSON.stringify(attribute.groupBy)) : undefined,
    transactionalFilters: {
      operator: attribute.selectedTransactionalFilters.operator ?? undefined,
      //@ts-ignore
      predicates: selectedFilters
    }
  };
}
